import {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  STIMULUS_CUSTOM_TYPE,
  STIMULUS_TYPE,
} from '@shared/Resources/Monitor/types';
import { V2_STIMULUS_PROGRESSION } from '@shared/Resources/Monitor/v2Monitors/types';
import { V2_RESULT_STATUS } from '@shared/Resources/Monitor/v2Monitors/runtime/types';
import {
  setActivityId,
  startMicrophone,
  stopMicrophone,
  VOICE_LOUDNESS_AUDIO_CONSTRAINTS,
} from '@shared/DSPHandler/services/MicrophoneService';
import StimuliModule from '../../containers/StimuliModule';
import {
  MonitorWrapper,
  ProgressWrapper,
  Wrapper,
} from '../../styles';
import V2MonitorHost from '../V2MonitorHost';
import V2RepetitionProgress from '../V2RepetitionProgress';

const resolveCurrentStimulus = ({
  exerciseId,
  stimulus,
  stimuli,
  stimuliEntityId,
  sequenceIndex,
}) => {
  if (stimulus.type === STIMULUS_TYPE.CUSTOM) {
    const { customText, customTexts, customType } = stimulus.config || {};
    const text = customType === STIMULUS_CUSTOM_TYPE.MULTIPLE
      && Array.isArray(customTexts)
      && customTexts.length
      ? customTexts[sequenceIndex % customTexts.length]
      : customText;
    return text ? { type: stimulus.type, text } : null;
  }

  return stimuliEntityId === exerciseId && stimuli?.length
    ? stimuli[sequenceIndex % stimuli.length]
    : null;
};

const V2ExerciseRuntime = ({
  activityContext,
  currentExercise,
  currentSequenceIndex,
  definition,
  exerciseInProgress,
  exercisePaused,
  microphoneStarted,
  monitorActive,
  onAdvanceRepetition,
  onAdvanceSequence,
  onPause,
  onSaveProduction,
  overlay,
  repetitionInSequenceIndex,
  repetitionsInSequence,
  stimuli,
  stimuliEntityId,
}) => {
  const hostRef = useRef(null);
  const finalizingRef = useRef(false);
  const pendingProductionRef = useRef(null);
  const [productions, setProductions] = useState([]);
  const { monitor, stimulus, progress } = currentExercise.monitor;
  const activityId = activityContext?.ACTIVITY_EXERCISE
    || activityContext?.ACTIVITY_LESSON;
  const currentStimulus = resolveCurrentStimulus({
    exerciseId: currentExercise.id,
    stimulus,
    stimuli,
    stimuliEntityId,
    sequenceIndex: currentSequenceIndex,
  });

  useEffect(() => {
    setProductions([]);
  }, [currentExercise.id]);

  useEffect(() => {
    if (activityId) setActivityId(activityId);
  }, [activityId]);

  useEffect(() => {
    if (!definition.requiresMicrophone) return undefined;

    if (microphoneStarted) {
      startMicrophone(() => {}, 'NO_ANALYSIS', false, VOICE_LOUDNESS_AUDIO_CONSTRAINTS);
    } else {
      stopMicrophone();
    }
    return () => stopMicrophone();
  }, [definition.requiresMicrophone, microphoneStarted]);

  const saveProduction = ({ monitor: productionMonitor, timing }) => onSaveProduction({
    activityContext,
    monitorType: productionMonitor.type,
    inhalationDuration: 0,
    dspSignal: [],
    startTS: timing.startedAt / 1000,
    endTS: timing.endedAt / 1000,
    productionErrors: [],
    stimuliDisplayed: [],
  });

  const handleNext = async () => {
    if (finalizingRef.current || !hostRef.current) return;
    finalizingRef.current = true;
    try {
      if (pendingProductionRef.current) {
        const pendingProduction = pendingProductionRef.current;
        await saveProduction(pendingProduction);
        setProductions((items) => [...items, pendingProduction]);
        pendingProductionRef.current = null;
        onAdvanceSequence();
        return;
      }

      const outcome = await hostRef.current.finishRepetition();
      if (outcome.status === V2_RESULT_STATUS.INCOMPLETE) return;

      if (outcome.productionCompleted) {
        pendingProductionRef.current = outcome.production;
        await saveProduction(outcome.production);
        setProductions((items) => [...items, outcome.production]);
        pendingProductionRef.current = null;
        onAdvanceSequence();
      } else {
        onAdvanceRepetition(repetitionInSequenceIndex + 1);
      }
    } finally {
      finalizingRef.current = false;
    }
  };

  const manualNext = definition.stimulusPolicy?.progression
    === V2_STIMULUS_PROGRESSION.MANUAL;

  return (
    <>
      <Wrapper>
        <MonitorWrapper withMirror={monitor.mirror.on} hasV2Monitor>
          <V2MonitorHost
            key={currentExercise.id}
            ref={hostRef}
            definition={definition}
            monitor={monitor}
            context={{
              active: exerciseInProgress
                && monitorActive
                && !exercisePaused
                && currentStimulus !== null,
              activityId,
              productionIndex: currentSequenceIndex,
              repetitionIndex: repetitionInSequenceIndex,
              repetitionCount: repetitionsInSequence,
              stimulus: currentStimulus,
            }}
            exercisePaused={exercisePaused}
            onPause={onPause}
          />
          {overlay}
        </MonitorWrapper>
      </Wrapper>
      <StimuliModule
        currentSequence={currentSequenceIndex}
        {...stimulus}
        progress={progress}
        currentExercise={currentExercise}
        productionCount={productions.length}
        manualNext={manualNext}
        onNext={manualNext ? handleNext : undefined}
      />
      <ProgressWrapper>
        <V2RepetitionProgress
          currentIndex={repetitionInSequenceIndex}
          repetitions={repetitionsInSequence}
        />
      </ProgressWrapper>
    </>
  );
};

V2ExerciseRuntime.propTypes = {
  activityContext: PropTypes.object,
  currentExercise: PropTypes.object.isRequired,
  currentSequenceIndex: PropTypes.number.isRequired,
  definition: PropTypes.object.isRequired,
  exerciseInProgress: PropTypes.bool.isRequired,
  exercisePaused: PropTypes.bool.isRequired,
  microphoneStarted: PropTypes.bool.isRequired,
  monitorActive: PropTypes.bool.isRequired,
  onAdvanceRepetition: PropTypes.func.isRequired,
  onAdvanceSequence: PropTypes.func.isRequired,
  onPause: PropTypes.func.isRequired,
  onSaveProduction: PropTypes.func.isRequired,
  overlay: PropTypes.node.isRequired,
  repetitionInSequenceIndex: PropTypes.number.isRequired,
  repetitionsInSequence: PropTypes.number.isRequired,
  stimuli: PropTypes.array,
  stimuliEntityId: PropTypes.string,
};

export default V2ExerciseRuntime;